import { useState, useEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import {
  FaCheck, FaTimes, FaInfoCircle, FaSearch, FaSync, FaPlus,
  FaEdit, FaTrash, FaBan, FaToggleOn, FaToggleOff, FaSave,
  FaEye, FaShieldAlt, FaExclamationTriangle, FaTimes as FaClose, FaTags,
  FaCalendarAlt
} from "react-icons/fa";
import { useTranslation } from "react-i18next";

const API = process.env.REACT_APP_API_URL || "http://localhost:5000";

/* ═══════════════════════════════════════════════════
   TOAST COMPONENT (Reused)
═══════════════════════════════════════════════════ */
function Toast({ message, type, onClose }) {
  const { t } = useTranslation();
  useEffect(() => {
    const timer = setTimeout(onClose, 5000);
    return () => clearTimeout(timer);
  }, [onClose]);

  const config = {
    success: { icon: <FaCheck />, label: t("toastBlessings") || "Success", accentColor: "#2a6b2a", iconBg: "rgba(42,107,42,0.12)", borderColor: "rgba(42,107,42,0.3)", symbol: "✦" },
    error:   { icon: <FaTimes />, label: t("toastError") || "Error", accentColor: "#8b1a1a", iconBg: "rgba(139,26,26,0.1)",  borderColor: "rgba(139,26,26,0.28)", symbol: "✗" },
    info:    { icon: <FaInfoCircle />, label: t("toastDivineNotice") || "Notice", accentColor: "#8a6020", iconBg: "rgba(201,151,58,0.1)", borderColor: "rgba(201,151,58,0.3)", symbol: "ॐ" }
  };
  const c = config[type] || config.info;

  return (
    <div className="adv-toast-container">
      <div className="adv-toast-card" style={{ "--accent": c.accentColor, "--border-color": c.borderColor }}>
        <div className="adv-toast-top-bar" />
        <span className="adv-toast-corner tl" /><span className="adv-toast-corner tr" />
        <span className="adv-toast-corner bl" /><span className="adv-toast-corner br" />
        <div className="adv-toast-medallion" style={{ background: c.iconBg, borderColor: c.borderColor }}>
          <div className="adv-toast-spin-ring" />
          <div className="adv-toast-icon-inner" style={{ color: c.accentColor }}>{c.icon}</div>
        </div>
        <div className="adv-toast-content">
          <div className="adv-toast-label-row">
            <span className="adv-toast-symbol" style={{ color: c.accentColor }}>{c.symbol}</span>
            <h4 className="adv-toast-title" style={{ color: c.accentColor }}>{c.label}</h4>
            <span className="adv-toast-symbol" style={{ color: c.accentColor }}>{c.symbol}</span>
          </div>
          <div className="adv-toast-rule" style={{ background: `linear-gradient(90deg,transparent,${c.accentColor}55,transparent)` }} />
          <p className="adv-toast-message">{message}</p>
        </div>
        <button className="adv-toast-close" onClick={onClose} style={{ color: c.accentColor }}>✕</button>
        <div className="adv-toast-bottom-rule" />
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════
   CONFIRM DIALOG (Reused)
═══════════════════════════════════════════════════ */
function ConfirmDialog({ message, onConfirm, onCancel, confirmLabel = "Confirm", danger = false, title = "Confirm Action", icon = null, subtitle = "" }) {
  const { t } = useTranslation();
  const iconEl = icon || (danger ? <FaExclamationTriangle /> : <FaShieldAlt />);
  const accentColor = danger ? "#8b1a1a" : "#2a6b2a";
  const accentBg = danger ? "rgba(139,26,26,0.12)" : "rgba(42,107,42,0.1)";
  const accentBorder = danger ? "rgba(139,26,26,0.3)" : "rgba(42,107,42,0.28)";

  return createPortal(
    <div className="adv-modal-overlay" onClick={onCancel}>
      <div className="adv-confirm-modal" onClick={e => e.stopPropagation()}>
        <div className="adv-cm-topstrip" style={{ background: `linear-gradient(90deg, transparent, ${accentColor}88, ${accentColor}, ${accentColor}88, transparent)` }} />
        <span className="adv-cm-corner tl" /><span className="adv-cm-corner tr" />
        <span className="adv-cm-corner bl" /><span className="adv-cm-corner br" />
        <div className="adv-cm-header">
          <div className="adv-cm-icon-wrap" style={{ background: accentBg, borderColor: accentBorder }}>
            <div className="adv-cm-icon-ring" style={{ borderColor: `${accentColor}44` }} />
            <div className="adv-cm-icon-ring adv-cm-icon-ring2" style={{ borderColor: `${accentColor}22` }} />
            <span className="adv-cm-icon" style={{ color: accentColor }}>{iconEl}</span>
          </div>
          <div className="adv-cm-title-area">
            <div className="adv-cm-eyebrow">✦ {t("modConfirmSub") || "Please Confirm"} ✦</div>
            <h3 className="adv-cm-title">{title}</h3>
            {subtitle && <p className="adv-cm-subtitle">{subtitle}</p>}
          </div>
        </div>
        <div className="adv-cm-divider">
          <span className="adv-cm-divider-gem" style={{ background: accentColor }} />
        </div>
        <div className="adv-cm-body">
          <p className="adv-cm-message">{message}</p>
        </div>
        <div className="adv-cm-actions">
          <button className="adv-btn adv-btn-ghost" onClick={onCancel}>{t("btnCancel") || "Cancel"}</button>
          <button
            className="adv-btn adv-cm-confirm-btn"
            style={{ "--cbg": danger ? "linear-gradient(135deg,#5a0000,#8b1a1a)" : "linear-gradient(135deg,#1a4a1a,#2a6b2a)", "--cborder": accentBorder }}
            onClick={onConfirm}
          >
            {iconEl} {confirmLabel}
          </button>
        </div>
        <div className="adv-cm-bottomstrip" />
      </div>
    </div>,
    document.body
  );
}

/* ═══════════════════════════════════════════════════
   EXPENSE CATEGORY FORM MODAL (Add/Edit)
═══════════════════════════════════════════════════ */
function CategoryFormModal({ category, onClose, onSaved, showToast }) {
  const { t } = useTranslation();
  const isEdit = !!category;

  const [formData, setFormData] = useState({
    title: category?.title || "",
    type: category?.type || "other",
    description: category?.description || "",
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: null }));
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.title.trim()) newErrors.title = t("expErrTitle") || "Title is required";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    setLoading(true);
    try {
      const payload = {
        title: formData.title.trim(),
        type: formData.type,
        description: formData.description.trim() || null,
      };

      const url = isEdit 
        ? `${API}/api/admin/expense-categories/${category.category_id}`
        : `${API}/api/admin/expense-categories`;

      const method = isEdit ? "PATCH" : "POST";

      const res = await fetch(url, {
        method,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Operation failed");

      showToast(isEdit ? (t("expToastUpdated") || "Category Updated") : (t("expToastCreated") || "Category Created"), "success");
      onSaved(data);
      onClose();
    } catch (err) {
      showToast(err.message, "error");
    } finally {
      setLoading(false);
    }
  };

  return createPortal(
    <div className="adv-modal-overlay" onClick={onClose}>
      <div className="adv-regal-modal adv-edit-regal" onClick={e => e.stopPropagation()}>
        <div className="adv-rm-topglow" />
        <span className="adv-cm-corner tl" /><span className="adv-cm-corner tr" />
        <span className="adv-cm-corner bl" /><span className="adv-cm-corner br" />
        <div className="adv-rm-header">
          <div className="adv-rm-header-bg" />
          <div className="adv-rm-header-inner">
            <div className="adv-rm-header-icon-wrap">
              <div className="adv-rm-hicon-ring" />
              <FaTags className="adv-rm-hicon" />
            </div>
            <div className="adv-rm-header-eyebrow">✦ {t("expModTitle") || "Expense Management"} ✦</div>
            <h2 className="adv-rm-title">{isEdit ? (t("expModEdit") || "Edit Category") : (t("expModAdd") || "Add Category")}</h2>
            <p className="adv-rm-name-tag">{isEdit ? category.title : (t("expModAddSub") || "Create a new expense classification")}</p>
          </div>
          <button className="adv-rm-close" onClick={onClose}><FaClose /></button>
        </div>
        <div className="adv-rm-rule"><span className="adv-rm-rule-ornament">❖</span></div>
        <div className="adv-edit-body">
          <div className="adv-edit-section-label">✦ {t("expSecDetails") || "Category Details"}</div>
          <div className="adv-edit-grid">
            <div className="adv-edit-field full-width">
              <label className="adv-edit-label">{t("expLblTitle")} <span className="adv-req">*</span></label>
              <div className={`adv-edit-wrap ${errors.title ? "err" : ""}`}>
                <input type="text" name="title" className="adv-edit-input" placeholder={t("expPhTitle") || "e.g. Festival Expenses"} value={formData.title} onChange={handleChange} />
              </div>
              {errors.title && <span className="adv-edit-err">{errors.title}</span>}
            </div>
            
            <div className="adv-edit-field">
              <label className="adv-edit-label">{t("expLblType")}</label>
              <div className="adv-edit-wrap">
                <select name="type" className="adv-edit-input" value={formData.type} onChange={handleChange}>
                  <option value="annual">Annual</option>
                  <option value="monthly">Monthly</option>
                  <option value="other">Other</option>
                </select>
              </div>
            </div>

            <div className="adv-edit-field full-width">
              <label className="adv-edit-label">{t("expLblDesc")}</label>
              <div className={`adv-edit-wrap adv-edit-wrap-textarea`}>
                <textarea name="description" className="adv-edit-textarea" placeholder={t("expPhDesc") || "Optional description..."} rows={3} value={formData.description} onChange={handleChange} />
              </div>
            </div>
          </div>
          <div className="adv-edit-actions">
            <button className="adv-btn adv-btn-ghost" onClick={onClose} disabled={loading}>{t("btnCancel") || "Cancel"}</button>
            <button className="adv-edit-save-btn" onClick={handleSubmit} disabled={loading || Object.keys(errors).length > 0}>
              {loading ? <><span className="adv-edit-saving-spinner" /> Saving...</> : <><FaSave /> {isEdit ? (t("expBtnUpdate") || "Update") : (t("expBtnCreate") || "Create")}</>}
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}

/* ═══════════════════════════════════════════════════
   MAIN COMPONENT: Aexpensecategory.js
═══════════════════════════════════════════════════ */
export default function AExpenseCategory() {
  const { t, i18n } = useTranslation();
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [toast, setToast] = useState(null);
  const [confirmDialog, setConfirmDialog] = useState(null);
  const [formModal, setFormModal] = useState(null); 
  const [logoFace, setLogoFace] = useState("om");



  useEffect(() => {
    const timer = setInterval(() => setLogoFace(f => f === "om" ? "logo" : "om"), 3000);
    return () => clearInterval(timer);
  }, []);

  const showToast = (message, type = "info") => setToast({ message, type });

  const fetchCategories = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/admin/expense-categories`);
      if (!res.ok) throw new Error("Failed to fetch");
      const data = await res.json();
      setCategories(data);
    } catch (err) {
      showToast(t("expErrFetch") || "Failed to load expense categories", "error");
    } finally {
      setLoading(false);
    }
  }, [API, t]);

  useEffect(() => { fetchCategories(); }, [fetchCategories]);

  // Filter logic
  const filtered = categories.filter(c => 
    c.title.toLowerCase().includes(search.toLowerCase()) ||
    (c.description && c.description.toLowerCase().includes(search.toLowerCase()))
  );

  // Handlers
  const handleAddClick = () => setFormModal({}); 
  const handleEditClick = (cat) => setFormModal(cat);
  
  const handleDelete = (cat) => {
    setConfirmDialog({
      title: t("expConfDeleteTitle") || "Delete Category",
      subtitle: t("expConfDeleteSub") || "This action cannot be undone",
      message: `${t("expConfDeleteMsg") || "Are you sure you want to delete"} "${cat.title}"?`,
      confirmLabel: t("expConfDeleteBtn") || "Delete Forever",
      danger: true,
      icon: <FaTrash />,
      onConfirm: async () => {
        try {
          const res = await fetch(`${API}/api/admin/expense-categories/${cat.category_id}`, { method: "DELETE" });
          if (!res.ok) throw new Error("Delete failed");
          showToast(t("expToastDeleted") || "Category deleted successfully", "success");
          setConfirmDialog(null);
          fetchCategories();
        } catch (err) {
          showToast(err.message, "error");
        }
      }
    });
  };

  const handleFormSaved = () => {
    setFormModal(null);
    fetchCategories();
  };

  // Stats
  const stats = {
    total: categories.length,
    annual: categories.filter(c => c.type === 'annual').length,
    monthly: categories.filter(c => c.type === 'monthly').length,
  };

  return (
    <>
      <DevoteesStyles />

      {/* HERO */}
      <div className="adv-hero">
        <div className="adv-hero-bg" />
        <div className="adv-hero-rings">
          {[220, 170, 125, 85].map((s, i) => (
            <div key={i} className="adv-hero-ring" style={{ width: s, height: s, animationDelay: `${i * 0.5}s` }} />
          ))}
        </div>
        <div className="adv-hero-medallion">
          <div className="adv-hero-med-ring" />
          <div className="adv-hero-med-ring adv-hero-med-ring-2" />
          <div className={`adv-hero-face ${logoFace === "om" ? "visible" : ""}`}>
            <span className="adv-hero-om">ॐ</span>
          </div>
          <div className={`adv-hero-face ${logoFace === "logo" ? "visible" : ""}`}>
            <img src="./logo.png" alt="Temple" className="adv-hero-logo-img" />
          </div>
          <div className="adv-hero-med-halo" />
        </div>
        <div className="adv-hero-text">
          <div className="adv-hero-eyebrow">✦ {t("templeName") || "Temple Admin"} ✦</div>
          <h1 className="adv-hero-title">{t("expcPageTitle") || "Expense Categories"}</h1>
          <p className="adv-hero-sub">{t("expPageSub") || "Manage temple expenditure classifications"}</p>
        </div>
        <div className="adv-hero-rule" />
        <div className="adv-stat-row">
          {[
            { label: t("expStatTotal") || "Total", val: stats.total, color: "#c9973a" },
            { label: t("expStatAnnual") || "Annual", val: stats.annual, color: "#2a6b2a" },
            { label: t("expStatMonthly") || "Monthly", val: stats.monthly, color: "#8a6020" },
          ].map((s, i) => (
            <div key={i} className="adv-stat-card" style={{ "--scolor": s.color }}>
              <div className="adv-stat-val">{s.val}</div>
              <div className="adv-stat-label">{s.label}</div>
            </div>
          ))}
        </div>
      </div>

      {/* TOOLBAR */}
      <div className="adv-toolbar">
        <div className="adv-search-wrap">
          <FaSearch className="adv-search-icon" />
          <input
            className="adv-search-input"
            placeholder={t("expSearchPlaceholder") || "Search categories..."}
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
          {search && <button className="adv-search-clear" onClick={() => setSearch("")}><FaClose /></button>}
        </div>

        <div className="adv-toolbar-btns" style={{ marginLeft: "auto" }}>
          <button className="adv-action-btn adv-btn-export" onClick={handleAddClick}>
            <FaPlus /> {t("expBtnAdd") || "Add Category"}
          </button>
          <button className="adv-action-btn adv-btn-refresh" onClick={fetchCategories}>
            <FaSync className={loading ? "spin" : ""} /> {t("btnRefresh") || "Refresh"}
          </button>
        </div>
      </div>

      {/* TABLE */}
      <div className="adv-table-wrap">
        {loading ? (
          <div className="adv-loader-wrap">
            <div className="adv-loader-rings">
              {[90, 70, 52].map((s, i) => (
                <div key={i} className="adv-loader-ring" style={{ width: s, height: s, animationDelay: `${i * 0.3}s` }} />
              ))}
              <div className="adv-loader-om">ॐ</div>
            </div>
            <p className="adv-loader-text">{t("expLoading") || "Loading categories..."}</p>
          </div>
        ) : filtered.length === 0 ? (
          <div className="adv-empty">
            <div className="adv-empty-om">ॐ</div>
            <h3 className="adv-empty-title">{t("expNoResultsTitle") || "No Categories Found"}</h3>
            <p className="adv-empty-sub">{t("expNoResultsSub") || "Create an expense category to get started."}</p>
          </div>
        ) : (
          <div className="adv-table-scroll-container">
          <table className="adv-table adv-desktop-only">
            <thead>
              <tr>
                <th>#</th><th>{t("expColTitle") || "Title"}</th><th>{t("expColType") || "Type"}</th><th>{t("expColDesc") || "Description"}</th>
                <th>{t("expColCreated") || "Created"}</th><th>{t("expColActions") || "Actions"}</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((c, i) => (
                <tr key={c.category_id} className="adv-table-row" style={{ animationDelay: `${i * 0.04}s` }}>
                  <td className="adv-td-num">{i + 1}</td>
                  <td className="adv-devotee-name">{c.title}</td>
                  <td className="adv-td-mobile" style={{ textTransform: 'capitalize' }}>
                    <span className={`adv-type-badge type-${c.type}`}>
                      {c.type}
                    </span>
                  </td>
                  <td className="adv-td-address">{c.description || <span style={{opacity:0.5, fontStyle:"italic"}}>—</span>}</td>
                  <td className="adv-td-date">
                    {new Date(c.created_at).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })}
                  </td>
                  <td>
                    <div className="adv-action-cell">
                      <button className="adv-icon-btn adv-btn-edit-sm" title={t("expModEdit") || "Edit"} onClick={() => handleEditClick(c)}><FaEdit /></button>
                      <button className="adv-icon-btn adv-btn-delete-sm" title={t("expConfDeleteTitle") || "Delete"} onClick={() => handleDelete(c)}><FaTrash /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {/* ── MOBILE CARDS ── */}
          <div className="adv-mobile-only adv-card-list">
            {filtered.map((c, i) => (
              <div key={c.category_id} className="adv-mob-card">
                <div className="adv-mob-card-header">
                  <div className="adv-mob-receipt">{c.title}</div>
                  <span className={`adv-type-badge type-${c.type}`} style={{ clipPath: 'none', borderRadius: 20, fontSize: '8px', padding: '3px 10px' }}>
                    {c.type}
                  </span>
                </div>
                <div className="adv-mob-meta">
                  {c.description && (
                    <div className="adv-mob-meta-item">
                      <FaTags className="adv-mob-meta-icon" />
                      <span>{c.description}</span>
                    </div>
                  )}
                  <div className="adv-mob-meta-item">
                    <FaCalendarAlt className="adv-mob-meta-icon" />
                    <span>{new Date(c.created_at).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })}</span>
                  </div>
                </div>
                <div className="adv-mob-actions">
                  <button className="adv-mob-btn mob-edit" onClick={() => handleEditClick(c)}><FaEdit /> {t("expModEdit") || "Edit"}</button>
                  <button className="adv-mob-btn mob-delete" onClick={() => handleDelete(c)}><FaTrash /></button>
                </div>
              </div>
            ))}
          </div>
          </div>
        )}
      </div>

      {/* MODALS */}
      {formModal && (
        <CategoryFormModal
          category={formModal.category_id ? formModal : null}
          onClose={() => setFormModal(null)}
          onSaved={handleFormSaved}
          showToast={showToast}
        />
      )}
      {confirmDialog && (
        <ConfirmDialog
          title={confirmDialog.title}
          subtitle={confirmDialog.subtitle}
          message={confirmDialog.message}
          confirmLabel={confirmDialog.confirmLabel}
          danger={confirmDialog.danger}
          icon={confirmDialog.icon}
          onConfirm={confirmDialog.onConfirm}
          onCancel={() => setConfirmDialog(null)}
        />
      )}
      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
    </>
  );
}

/* ═══════════════════════════════════════════════════
   STYLES (Reused from Acategory.js + Additions)
═══════════════════════════════════════════════════ */
function DevoteesStyles() {
  return (
    <style>{`
      @import url('https://fonts.googleapis.com/css2?family=Cinzel+Decorative:wght@400;700;900&family=Cinzel:wght@400;500;600;700&family=EB+Garamond:ital,wght@0,400;0,500;0,600;1,400;1,500&display=swap');

      :root {
        --gold-bright: #e8c46a; --gold-mid: #c9973a; --gold-dim: #8a6020;
        --crimson-deep: #1a0404; --crimson-mid: #2c0808; --crimson-rich: #3d0c0c;
        --hero-1: #2e0c0c; --hero-2: #1c0505; --hero-3: #100202;
        --parchment: #FDF3E3; --parchment-dark: #f5e8cc;
        --text-dark: #1a0404; --text-mid: #4a1a08;
      }

      /* ── HERO ── */
      .adv-hero { position: relative; overflow: hidden; background: linear-gradient(160deg, var(--hero-1) 0%, var(--hero-2) 50%, var(--hero-3) 100%); padding: 40px 32px 0; display: flex; flex-direction: column; align-items: center; border-radius: 16px 16px 0 0; }
      .adv-hero-bg { position: absolute; inset: 0; pointer-events: none; background: radial-gradient(ellipse at 15% 30%, rgba(160,35,35,0.35) 0%, transparent 50%), radial-gradient(ellipse at 85% 70%, rgba(80,15,15,0.3) 0%, transparent 45%); }
      .adv-hero-rings { position: absolute; top: 30px; right: 60px; width: 230px; height: 230px; pointer-events: none; }
      .adv-hero-ring { position: absolute; top: 50%; left: 50%; transform: translate(-50%, -50%); border-radius: 50%; border: 1px solid rgba(232,196,106,0.07); animation: heroRingPulse 4s ease-in-out infinite alternate; }
      .adv-hero-ring:nth-child(odd) { border-style: dashed; }
      @keyframes heroRingPulse { from { opacity: 0.3; } to { opacity: 0.8; } }
      .adv-hero-medallion { width: 90px; height: 90px; border-radius: 50%; background: radial-gradient(circle at 40% 35%, #5a1515 0%, #2a0707 55%, #0e0101 100%); border: 1px solid rgba(232,196,106,0.3); display: flex; align-items: center; justify-content: center; position: relative; margin-bottom: 20px; box-shadow: 0 0 30px rgba(232,196,106,0.15), inset 0 1px 0 rgba(255,248,231,0.06); }
      .adv-hero-med-ring { position: absolute; inset: -5px; border-radius: 50%; border: 1px dashed rgba(232,196,106,0.25); animation: slowSpin 10s linear infinite; }
      .adv-hero-med-ring-2 { inset: -10px; border-style: dotted; border-color: rgba(232,196,106,0.12); animation-duration: 18s; animation-direction: reverse; }
      @keyframes slowSpin { to { transform: rotate(360deg); } }
      .adv-hero-face { position: absolute; inset: 0; border-radius: 50%; display: flex; align-items: center; justify-content: center; opacity: 0; transform: scale(0.7) rotateY(90deg); transition: opacity 0.55s ease, transform 0.55s ease; }
      .adv-hero-face.visible { opacity: 1; transform: scale(1) rotateY(0deg); }
      .adv-hero-om { font-family: 'Cinzel Decorative', cursive; font-size: 34px; background: linear-gradient(180deg, #fff8e7, var(--gold-bright), var(--gold-mid)); -webkit-background-clip: text; -webkit-text-fill-color: transparent; filter: drop-shadow(0 0 8px rgba(232,196,106,0.5)); }
      .adv-hero-logo-img { width: 54px; height: 54px; object-fit: contain; filter: drop-shadow(0 0 8px rgba(232,196,106,0.4)); }
      .adv-hero-med-halo { position: absolute; inset: -16px; border-radius: 50%; background: radial-gradient(circle, rgba(232,196,106,0.08) 0%, transparent 65%); pointer-events: none; }
      .adv-hero-text { text-align: center; position: relative; z-index: 1; }
      .adv-hero-eyebrow { font-family: 'Cinzel', serif; font-size: 8.5px; letter-spacing: 2px; color: rgba(232,196,106,0.35); text-transform: uppercase; margin-bottom: 10px; }
      .adv-hero-title { font-family: 'Cinzel Decorative', cursive; font-size: 28px; font-weight: 900; background: linear-gradient(135deg, #fffaf0, var(--gold-bright), var(--gold-mid)); -webkit-background-clip: text; -webkit-text-fill-color: transparent; line-height: 1.2; margin-bottom: 8px; }
      .adv-hero-sub { font-family: 'EB Garamond', serif; font-style: italic; font-size: 15px; color: rgba(245,228,176,0.4); margin-bottom: 20px; }
      .adv-hero-rule { width: 60%; max-width: 400px; height: 1px; margin: 0 auto 24px; background: linear-gradient(90deg, transparent, rgba(201,151,58,0.4), transparent); }
      .adv-stat-row { display: flex; gap: 14px; flex-wrap: wrap; justify-content: center; padding: 0 0 28px; position: relative; z-index: 1; }
      .adv-stat-card { background: rgba(255,255,255,0.04); border: 1px solid rgba(201,151,58,0.18); padding: 14px 22px; text-align: center; clip-path: polygon(10px 0%, 100% 0%, calc(100% - 10px) 100%, 0% 100%); transition: background 0.2s, border-color 0.2s; min-width: 90px; }
      .adv-stat-card:hover { background: rgba(201,151,58,0.08); border-color: rgba(201,151,58,0.35); }
      .adv-stat-val { font-family: 'Cinzel Decorative', cursive; font-size: 22px; font-weight: 900; color: var(--scolor, var(--gold-bright)); }
      .adv-stat-label { font-family: 'Cinzel', serif; font-size: 8px; letter-spacing: 1.5px; text-transform: uppercase; color: rgba(245,228,176,0.4); margin-top: 3px; }

      /* ── TOOLBAR ── */
      .adv-toolbar { display: flex; flex-wrap: wrap; gap: 12px; align-items: center; padding: 20px 28px 12px; background: var(--parchment); border-bottom: 1px solid rgba(201,151,58,0.15); position: sticky; top: 0; z-index: 10; box-shadow: 0 4px 12px rgba(26,4,4,0.06); }
      .adv-search-wrap { flex: 1; min-width: 220px; display: flex; align-items: center; background: #fff9f0; border: 1px solid rgba(201,151,58,0.28); clip-path: polygon(10px 0%, calc(100% - 10px) 0%, 100% 50%, calc(100% - 10px) 100%, 10px 100%, 0% 50%); overflow: hidden; transition: border-color 0.2s, box-shadow 0.2s; }
      .adv-search-wrap:focus-within { border-color: var(--gold-mid); box-shadow: 0 0 0 3px rgba(201,151,58,0.1); }
      .adv-search-icon { margin: 0 10px 0 18px; color: var(--gold-dim); font-size: 13px; flex-shrink: 0; }
      .adv-search-input { flex: 1; border: none; outline: none; background: transparent; font-family: 'EB Garamond', serif; font-size: 15px; color: var(--text-dark); padding: 11px 8px; }
      .adv-search-input::placeholder { color: rgba(74,26,8,0.28); font-style: italic; }
      .adv-search-clear { background: none; border: none; cursor: pointer; padding: 0 14px; color: rgba(74,26,8,0.35); font-size: 11px; transition: color 0.2s; }
      .adv-search-clear:hover { color: #8b1a1a; }
      .adv-toolbar-btns { display: flex; gap: 8px; margin-left: auto; }
      .adv-action-btn { display: flex; align-items: center; gap: 7px; font-family: 'Cinzel', serif; font-size: 9.5px; font-weight: 700; letter-spacing: 1.5px; text-transform: uppercase; border: none; padding: 10px 18px; cursor: pointer; clip-path: polygon(10px 0%, calc(100% - 10px) 0%, 100% 50%, calc(100% - 10px) 100%, 10px 100%, 0% 50%); transition: all 0.25s; }
      .adv-btn-refresh { background: rgba(201,151,58,0.1); border: 1px solid rgba(201,151,58,0.3); color: var(--gold-dim); }
      .adv-btn-refresh:hover { background: rgba(201,151,58,0.18); }
      .adv-btn-export { background: linear-gradient(135deg, #1a4d1a, #2a6b2a); color: #c8f0c8; border: 1px solid rgba(42,107,42,0.4); }
      .adv-btn-export:hover { filter: brightness(1.1); }
      .spin { animation: spinIcon 0.8s linear infinite; }
      @keyframes spinIcon { to { transform: rotate(360deg); } }

      /* ── TABLE ── */
      .adv-table-wrap { padding: 0 28px 40px; background: var(--parchment); overflow-x: auto; }
      .adv-table { width: 100%; border-collapse: separate; border-spacing: 0 6px; font-family: 'EB Garamond', serif; }
      .adv-table thead tr { background: linear-gradient(135deg, var(--hero-1) 0%, var(--hero-2) 55%, var(--hero-3) 100%); }
      .adv-table thead tr th { font-family: 'Cinzel', serif; font-size: 8.5px; font-weight: 700; letter-spacing: 2px; text-transform: uppercase; color: var(--gold-bright); padding: 14px 14px; text-align: left; border-bottom: 1px solid rgba(201,151,58,0.25); white-space: nowrap; position: relative; }
      .adv-table thead tr th:first-child { border-radius: 3px 0 0 3px; padding-left: 18px; }
      .adv-table thead tr th:last-child { border-radius: 0 3px 3px 0; }
      .adv-table thead tr th::after { content: ''; position: absolute; bottom: 0; left: 0; right: 0; height: 1px; background: linear-gradient(90deg, transparent, rgba(201,151,58,0.4), transparent); }
      .adv-table-row { background: linear-gradient(135deg, #FDF3E3, #faf0dc); border: 1px solid rgba(201,151,58,0.15); animation: rowIn 0.35s ease both; transition: box-shadow 0.2s, transform 0.2s; }
      .adv-table-row:hover { box-shadow: 0 4px 18px rgba(26,4,4,0.1); transform: translateY(-1px); }
      @keyframes rowIn { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }
      .adv-table-row td { padding: 12px 14px; vertical-align: middle; font-size: 14px; color: var(--text-mid); border-top: 1px solid rgba(201,151,58,0.1); border-bottom: 1px solid rgba(201,151,58,0.1); }
      .adv-table-row td:first-child { border-left: 1px solid rgba(201,151,58,0.1); border-radius: 2px 0 0 2px; }
      .adv-table-row td:last-child  { border-right: 1px solid rgba(201,151,58,0.1); border-radius: 0 2px 2px 0; }
      .adv-td-num { font-family: 'Cinzel', serif; font-size: 10px; color: rgba(74,26,8,0.35); width: 36px; }
      .adv-td-mobile { font-family: 'Cinzel', serif; font-size: 11px; letter-spacing: 0.5px; }
      .adv-td-date { font-family: 'Cinzel', serif; font-size: 10px; color: rgba(74,26,8,0.5); white-space: nowrap; }
      .adv-td-address { font-family: 'EB Garamond', serif; font-size: 13px; color: rgba(74,26,8,0.6); max-width: 180px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
      .adv-devotee-name { font-family: 'Cinzel', serif; font-size: 11px; font-weight: 700; letter-spacing: 0.5px; color: var(--text-dark); }
      .adv-action-cell { display: flex; gap: 5px; align-items: center; flex-wrap: wrap; }
      .adv-icon-btn { width: 30px; height: 30px; border: 1px solid; border-radius: 2px; display: flex; align-items: center; justify-content: center; font-size: 12px; cursor: pointer; background: none; transition: all 0.18s; flex-shrink: 0; }
      .adv-btn-view           { border-color: rgba(201,151,58,0.3); color: var(--gold-dim); }
      .adv-btn-view:hover     { background: rgba(201,151,58,0.12); color: var(--gold-mid); }
      .adv-btn-edit-sm        { border-color: rgba(100,74,140,0.35); color: #6a4a8c; }
      .adv-btn-edit-sm:hover  { background: rgba(100,74,140,0.1); }
      .adv-btn-delete-sm        { border-color: rgba(139,26,26,0.25); color: rgba(139,26,26,0.7); }
      .adv-btn-delete-sm:hover  { background: rgba(139,26,26,0.08); color: #8b1a1a; }
      
      /* Expense Type Badges */
      .adv-type-badge { font-family: 'Cinzel', serif; font-size: 8.5px; font-weight: 700; letter-spacing: 1.5px; text-transform: uppercase; padding: 5px 12px; border: 1px solid; display: inline-flex; align-items: center; gap: 6px; clip-path: polygon(8px 0%,calc(100% - 8px) 0%,100% 50%,calc(100% - 8px) 100%,8px 100%,0 50%); }
      .type-annual { color: #8a6020; background: rgba(138,96,32,0.1); border-color: rgba(138,96,32,0.3); }
      .type-monthly { color: #2a6b2a; background: rgba(42,107,42,0.1); border-color: rgba(42,107,42,0.3); }
      .type-other { color: #4a1a08; background: rgba(74,26,8,0.1); border-color: rgba(74,26,8,0.3); }

      /* ── LOADER & EMPTY ── */
      .adv-loader-wrap { display: flex; flex-direction: column; align-items: center; justify-content: center; padding: 80px 0; gap: 14px; }
      .adv-loader-rings { position: relative; width: 100px; height: 100px; margin-bottom: 10px; }
      .adv-loader-ring { position: absolute; top: 50%; left: 50%; transform: translate(-50%,-50%); border-radius: 50%; border: 1px dashed rgba(201,151,58,0.3); animation: loaderPulse 2s ease-in-out infinite; }
      .adv-loader-ring:nth-child(2) { border-style: solid; border-color: rgba(201,151,58,0.15); }
      .adv-loader-ring:nth-child(3) { animation: slowSpin 3s linear infinite; border-color: rgba(201,151,58,0.25); }
      @keyframes loaderPulse { 0%,100% { opacity: 0.3; transform: translate(-50%,-50%) scale(0.95); } 50% { opacity: 1; transform: translate(-50%,-50%) scale(1.02); } }
      .adv-loader-om { position: absolute; top: 50%; left: 50%; transform: translate(-50%,-50%); font-family: 'Cinzel Decorative', cursive; font-size: 28px; background: linear-gradient(180deg, #fff8e7, var(--gold-bright), var(--gold-mid)); -webkit-background-clip: text; -webkit-text-fill-color: transparent; animation: omGlow 2s ease-in-out infinite alternate; }
      @keyframes omGlow { from { filter: drop-shadow(0 0 4px rgba(232,196,106,0.3)); } to { filter: drop-shadow(0 0 12px rgba(232,196,106,0.7)); } }
      .adv-loader-text { font-family: 'EB Garamond', serif; font-style: italic; font-size: 15px; color: rgba(74,26,8,0.4); }
      .adv-empty { display: flex; flex-direction: column; align-items: center; justify-content: center; padding: 70px 0; gap: 14px; text-align: center; }
      .adv-empty-om { font-family: 'Cinzel Decorative', cursive; font-size: 52px; background: linear-gradient(180deg, var(--gold-bright), var(--gold-mid)); -webkit-background-clip: text; -webkit-text-fill-color: transparent; opacity: 0.25; }
      .adv-empty-title { font-family: 'Cinzel Decorative', cursive; font-size: 20px; background: linear-gradient(135deg, var(--crimson-rich), var(--crimson-deep)); -webkit-background-clip: text; -webkit-text-fill-color: transparent; }
      .adv-empty-sub { font-family: 'EB Garamond', serif; font-style: italic; font-size: 15px; color: rgba(74,26,8,0.45); max-width: 320px; }

      /* ── BUTTONS ── */
      .adv-btn { display: inline-flex; align-items: center; gap: 7px; font-family: 'Cinzel', serif; font-size: 9.5px; font-weight: 700; letter-spacing: 1.5px; text-transform: uppercase; border: none; padding: 10px 18px; cursor: pointer; clip-path: polygon(10px 0%,calc(100% - 10px) 0%,100% 50%,calc(100% - 10px) 100%,10px 100%,0 50%); transition: all 0.2s; border: 1px solid transparent; }
      .adv-btn:disabled { opacity: 0.38; cursor: not-allowed; }
      .adv-btn-gold { background: linear-gradient(135deg,#7a4e0c,var(--gold-mid),var(--gold-bright),#fff8e7,var(--gold-bright),var(--gold-mid),#6b4209); color: var(--crimson-deep); }
      .adv-btn-ghost { background: none; border-color: rgba(201,151,58,0.28); color: rgba(74,26,8,0.5); }
      .adv-btn-ghost:hover { border-color: rgba(201,151,58,0.5); background: rgba(201,151,58,0.06); }

      /* ── MODAL OVERLAY ── */
      .adv-modal-overlay { position: fixed; inset: 0; z-index: 1000; background: rgba(8,1,1,0.85); backdrop-filter: blur(7px); display: flex; align-items: center; justify-content: center; padding: 20px; animation: overlayIn 0.2s ease; }
      @keyframes overlayIn { from { opacity: 0; } to { opacity: 1; } }

      /* ── REGAL MODAL BASE ── */
      .adv-regal-modal { position: relative; width: 100%; background: linear-gradient(160deg, #FDF3E3 0%, #faf0dc 55%, #f5e8cc 100%); border: 1px solid rgba(201,151,58,0.35); box-shadow: 0 36px 110px rgba(26,4,4,0.7), 0 0 0 1px rgba(201,151,58,0.06) inset; animation: regalIn 0.35s cubic-bezier(0.23,1,0.32,1) both; overflow: hidden; }
      @keyframes regalIn { from { opacity: 0; transform: scale(0.93) translateY(26px); } to { opacity: 1; transform: scale(1) translateY(0); } }
      .adv-detail-regal { max-width: 540px; max-height: 90vh; overflow-y: auto; }
      .adv-detail-regal::-webkit-scrollbar { width: 6px; }
      .adv-detail-regal::-webkit-scrollbar-track { background: linear-gradient(180deg, #f5e8cc, #faf0dc); border-radius: 10px; }
      .adv-detail-regal::-webkit-scrollbar-thumb { background: linear-gradient(180deg, var(--hero-1), #5a1515, var(--gold-mid)); border-radius: 10px; border: 1px solid rgba(232,196,106,0.3); box-shadow: 0 0 6px rgba(232,196,106,0.25); }
      .adv-idproof-regal { max-width: 700px; max-height: 80vh; display: flex; flex-direction: column; }
      .adv-edit-regal { max-width: 580px; max-height: 92vh; overflow-y: auto; }
      .adv-edit-regal::-webkit-scrollbar { width: 6px; }
      .adv-edit-regal::-webkit-scrollbar-track { background: linear-gradient(180deg, #f5e8cc, #faf0dc); border-radius: 10px; }
      .adv-edit-regal::-webkit-scrollbar-thumb { background: linear-gradient(180deg, var(--hero-1), #5a1515, var(--gold-mid)); border-radius: 10px; border: 1px solid rgba(232,196,106,0.3); }

      .adv-rm-topglow { position: absolute; top: 0; left: 0; right: 0; height: 3px; background: linear-gradient(90deg, transparent 0%, rgba(201,151,58,0.2) 20%, rgba(232,196,106,0.9) 50%, rgba(201,151,58,0.2) 80%, transparent 100%); z-index: 10; }
      .adv-cm-corner { position: absolute; width: 16px; height: 16px; border-color: rgba(201,151,58,0.5); border-style: solid; z-index: 10; }
      .adv-cm-corner.tl { top: 14px; left: 14px; border-width: 1.5px 0 0 1.5px; }
      .adv-cm-corner.tr { top: 14px; right: 14px; border-width: 1.5px 1.5px 0 0; }
      .adv-cm-corner.bl { bottom: 14px; left: 14px; border-width: 0 0 1.5px 1.5px; }
      .adv-cm-corner.br { bottom: 14px; right: 14px; border-width: 0 1.5px 1.5px 0; }

      .adv-rm-header { position: relative; overflow: hidden; background: linear-gradient(160deg, var(--hero-1), var(--hero-2) 55%, var(--hero-3)); padding: 38px 28px 28px; text-align: center; border-bottom: 1px solid rgba(201,151,58,0.2); }
      .adv-rm-header-bg { position: absolute; inset: 0; background: radial-gradient(ellipse at 50% 40%, rgba(150,30,30,0.28) 0%, transparent 65%); pointer-events: none; }
      .adv-rm-header-inner { position: relative; z-index: 1; display: flex; flex-direction: column; align-items: center; gap: 8px; }
      .adv-rm-close { position: absolute; top: 16px; right: 16px; z-index: 5; background: rgba(255,255,255,0.06); border: 1px solid rgba(232,196,106,0.2); color: rgba(232,196,106,0.55); border-radius: 50%; width: 30px; height: 30px; display: flex; align-items: center; justify-content: center; cursor: pointer; font-size: 11px; transition: all 0.2s; }
      .adv-rm-close:hover { background: rgba(255,255,255,0.12); color: var(--gold-bright); transform: rotate(90deg); }
      .adv-rm-header-icon-wrap { position: relative; width: 72px; height: 72px; border-radius: 50%; display: flex; align-items: center; justify-content: center; background: rgba(232,196,106,0.08); border: 1px solid rgba(232,196,106,0.2); }
      .adv-rm-hicon-ring { position: absolute; inset: -5px; border-radius: 50%; border: 1px dashed rgba(232,196,106,0.28); animation: slowSpin 9s linear infinite; }
      .adv-rm-hicon { font-size: 28px; color: var(--gold-bright); filter: drop-shadow(0 0 10px rgba(232,196,106,0.4)); position: relative; z-index: 1; }
      .adv-rm-header-eyebrow { font-family: 'Cinzel', serif; font-size: 7.5px; letter-spacing: 3px; color: rgba(232,196,106,0.3); text-transform: uppercase; }
      .adv-rm-title { font-family: 'Cinzel Decorative', cursive; font-size: 22px; font-weight: 900; background: linear-gradient(135deg, #fffaf0, var(--gold-bright), var(--gold-mid)); -webkit-background-clip: text; -webkit-text-fill-color: transparent; line-height: 1.25; }
      .adv-rm-name-tag { font-family: 'Cinzel', serif; font-size: 11px; letter-spacing: 1px; color: rgba(245,228,176,0.4); }
      .adv-rm-status-pill { font-family: 'Cinzel', serif; font-size: 8.5px; font-weight: 700; letter-spacing: 2px; text-transform: uppercase; padding: 5px 16px; border: 1px solid; display: flex; align-items: center; gap: 6px; clip-path: polygon(8px 0%,calc(100% - 8px) 0%,100% 50%,calc(100% - 8px) 100%,8px 100%,0 50%); }
      .adv-rm-rule { display: flex; align-items: center; justify-content: center; height: 1px; margin: 0 40px; background: linear-gradient(90deg, transparent, rgba(201,151,58,0.4), transparent); position: relative; z-index: 1; }
      .adv-rm-rule-ornament { position: absolute; font-size: 12px; color: var(--gold-mid); background: var(--parchment); padding: 0 8px; line-height: 1; }

      /* ── EDIT FORM ── */
      .adv-edit-body { padding: 22px 28px 28px; position: relative; z-index: 1; }
      .adv-edit-section-label { font-family: 'Cinzel', serif; font-size: 8px; font-weight: 700; letter-spacing: 2.5px; text-transform: uppercase; color: var(--gold-dim); margin-bottom: 18px; padding-bottom: 7px; border-bottom: 1px solid rgba(201,151,58,0.15); }
      .adv-edit-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 14px; margin-bottom: 20px; }
      .adv-edit-grid .full-width { grid-column: span 2; }
      .adv-edit-field { display: flex; flex-direction: column; gap: 5px; }
      .adv-edit-label { font-family: 'Cinzel', serif; font-size: 8.5px; font-weight: 700; letter-spacing: 2px; text-transform: uppercase; color: var(--gold-dim); }
      .adv-req { color: #b52020; font-weight: 900; margin-left: 2px; }
      .adv-edit-wrap { display: flex; align-items: center; background: #fff9f0; border: 1px solid rgba(201,151,58,0.28); clip-path: polygon(10px 0%, calc(100% - 10px) 0%, 100% 50%, calc(100% - 10px) 100%, 10px 100%, 0% 50%); overflow: hidden; transition: border-color 0.3s, box-shadow 0.3s; }
      .adv-edit-wrap:focus-within { border-color: var(--gold-mid); background: #fffaf0; box-shadow: 0 0 0 3px rgba(201,151,58,0.12); }
      .adv-edit-wrap.err { border-color: #b52020; background: rgba(181,32,32,0.02); }
      .adv-edit-wrap-textarea { clip-path: polygon(10px 0%, calc(100% - 10px) 0%, 100% 10px, 100% calc(100% - 10px), calc(100% - 10px) 100%, 10px 100%, 0 calc(100% - 10px), 0 10px); align-items: flex-start; }
      .adv-edit-input { flex: 1; border: none; outline: none; font-family: 'EB Garamond', serif; font-size: 15px; color: var(--text-dark); padding: 13px 14px; background: transparent; width: 100%; }
      .adv-edit-input::placeholder { color: rgba(74,26,8,0.25); font-style: italic; }
      .adv-edit-textarea { flex: 1; border: none; outline: none; font-family: 'EB Garamond', serif; font-size: 15px; color: var(--text-dark); padding: 13px 14px; resize: vertical; min-height: 80px; line-height: 1.5; }
      .adv-edit-textarea::placeholder { color: rgba(74,26,8,0.25); font-style: italic; }
      .adv-edit-err { font-size: 10px; color: #b52020; font-family: 'EB Garamond', serif; font-style: italic; margin-top: 2px; }
      .adv-edit-actions { display: flex; gap: 10px; align-items: center; justify-content: center; margin-top: 4px; padding-top: 18px; border-top: 1px solid rgba(201,151,58,0.12); }
      .adv-edit-save-btn { display: inline-flex; align-items: center; gap: 8px; font-family: 'Cinzel', serif; font-size: 10px; font-weight: 700; letter-spacing: 2px; text-transform: uppercase; color: var(--crimson-deep); background: linear-gradient(135deg, #7a4e0c 0%, var(--gold-mid) 20%, var(--gold-bright) 40%, #fff8e7 55%, var(--gold-bright) 70%, var(--gold-mid) 85%, #6b4209 100%); border: none; padding: 13px 28px; cursor: pointer; clip-path: polygon(14px 0%, calc(100% - 14px) 0%, 100% 50%, calc(100% - 14px) 100%, 14px 100%, 0 50%); box-shadow: 0 6px 24px rgba(201,151,58,0.3), inset 0 1px 0 rgba(255,255,255,0.25); transition: all 0.35s ease; position: relative; overflow: hidden; }
      .adv-edit-save-btn::before { content: ''; position: absolute; top: -50%; left: -70%; width: 40%; height: 200%; background: rgba(255,255,255,0.25); transform: skewX(-18deg); transition: left 0.55s ease; pointer-events: none; }
      .adv-edit-save-btn:hover::before { left: 130%; }
      .adv-edit-save-btn:hover { transform: translateY(-2px); box-shadow: 0 12px 32px rgba(201,151,58,0.5); filter: brightness(1.05); }
      .adv-edit-save-btn:disabled { opacity: 0.38; cursor: not-allowed; transform: none; box-shadow: none; }
      .adv-edit-saving-spinner { width: 12px; height: 12px; border-radius: 50%; border: 2px solid rgba(26,4,4,0.2); border-top-color: var(--crimson-deep); animation: spinIcon 0.7s linear infinite; flex-shrink: 0; }

      /* ── CONFIRM MODAL ── */
      .adv-confirm-modal { position: relative; width: 100%; max-width: 420px; background: linear-gradient(160deg, #FDF3E3, #faf0dc 55%, #f5e8cc); border: 1px solid rgba(201,151,58,0.35); clip-path: polygon(0 18px,18px 0,calc(100% - 18px) 0,100% 18px,100% calc(100% - 18px),calc(100% - 18px) 100%,18px 100%,0 calc(100% - 18px)); box-shadow: 0 32px 90px rgba(26,4,4,0.68); animation: regalIn 0.3s cubic-bezier(0.23,1,0.32,1) both; overflow: hidden; }
      .adv-cm-topstrip { height: 3px; width: 100%; }
      .adv-cm-header { padding: 30px 28px 0; display: flex; flex-direction: column; align-items: center; text-align: center; gap: 10px; position: relative; z-index: 1; }
      .adv-cm-icon-wrap { position: relative; width: 68px; height: 68px; border-radius: 50%; display: flex; align-items: center; justify-content: center; border: 1px solid; }
      .adv-cm-icon-ring { position: absolute; inset: -5px; border-radius: 50%; border: 1px dashed; animation: slowSpin 8s linear infinite; }
      .adv-cm-icon-ring2 { inset: -12px; animation-duration: 15s; animation-direction: reverse; }
      .adv-cm-icon { font-size: 24px; position: relative; z-index: 1; }
      .adv-cm-title-area { display: flex; flex-direction: column; align-items: center; gap: 4px; }
      .adv-cm-eyebrow { font-family: 'Cinzel', serif; font-size: 7px; letter-spacing: 3px; color: rgba(74,26,8,0.3); text-transform: uppercase; }
      .adv-cm-title { font-family: 'Cinzel Decorative', cursive; font-size: 20px; font-weight: 900; background: linear-gradient(135deg, var(--hero-1), var(--hero-2)); -webkit-background-clip: text; -webkit-text-fill-color: transparent; }
      .adv-cm-subtitle { font-family: 'EB Garamond', serif; font-style: italic; font-size: 12.5px; color: rgba(74,26,8,0.38); }
      .adv-cm-divider { display: flex; align-items: center; justify-content: center; height: 1px; margin: 18px 36px 0; background: linear-gradient(90deg, transparent, rgba(201,151,58,0.35), transparent); position: relative; }
      .adv-cm-divider-gem { width: 6px; height: 6px; transform: rotate(45deg); position: absolute; }
      .adv-cm-body { padding: 16px 32px 20px; position: relative; z-index: 1; }
      .adv-cm-message { font-family: 'EB Garamond', serif; font-size: 16px; color: var(--text-mid); line-height: 1.7; text-align: center; }
      .adv-cm-actions { display: flex; gap: 10px; justify-content: center; flex-wrap: wrap; padding: 0 28px 24px; position: relative; z-index: 1; }
      .adv-cm-confirm-btn { background: var(--cbg, linear-gradient(135deg,#1a4a1a,#2a6b2a)) !important; color: #e8ffe8 !important; border-color: var(--cborder, rgba(42,107,42,0.4)) !important; }
      .adv-cm-confirm-btn:hover { filter: brightness(1.12); }
      .adv-cm-bottomstrip { height: 1px; margin: 0 24px 14px; background: linear-gradient(90deg, transparent, rgba(201,151,58,0.25), transparent); }

      /* ── TOAST ── */
      .adv-toast-container { position: fixed; top: 28px; right: 28px; z-index: 2000; animation: toastIn 0.4s cubic-bezier(0.23,1,0.32,1) both; }
      @keyframes toastIn { from { opacity: 0; transform: translateX(60px) scale(0.95); } to { opacity: 1; transform: translateX(0) scale(1); } }
      .adv-toast-card { position: relative; width: 340px; background: linear-gradient(145deg,#FDF3E3,#faf0dc,#f5e8cc); border: 1px solid var(--border-color); clip-path: polygon(0 12px,12px 0,calc(100% - 12px) 0,100% 12px,100% calc(100% - 12px),calc(100% - 12px) 100%,12px 100%,0 calc(100% - 12px)); box-shadow: 0 12px 32px rgba(26,4,4,0.14), inset 0 1px 0 rgba(255,255,255,0.7); padding: 0 0 14px; overflow: hidden; }
      .adv-toast-top-bar { height: 3px; background: linear-gradient(90deg,transparent,var(--accent) 20%,var(--accent) 80%,transparent); margin-bottom: 16px; }
      .adv-toast-corner { position: absolute; width: 10px; height: 10px; border-color: rgba(201,151,58,0.45); border-style: solid; }
      .adv-toast-corner.tl { top: 12px; left: 12px; border-width: 1px 0 0 1px; }
      .adv-toast-corner.tr { top: 12px; right: 12px; border-width: 1px 1px 0 0; }
      .adv-toast-corner.bl { bottom: 8px; left: 12px; border-width: 0 0 1px 1px; }
      .adv-toast-corner.br { bottom: 8px; right: 12px; border-width: 0 1px 1px 0; }
      .adv-toast-medallion { position: absolute; left: 18px; top: 26px; width: 36px; height: 36px; border-radius: 50%; border: 1px solid; display: flex; align-items: center; justify-content: center; }
      .adv-toast-spin-ring { position: absolute; inset: -3px; border-radius: 50%; border: 1px dashed rgba(201,151,58,0.3); animation: slowSpin 8s linear infinite; }
      .adv-toast-icon-inner { font-size: 14px; position: relative; z-index: 1; }
      .adv-toast-content { padding: 0 40px 0 66px; }
      .adv-toast-label-row { display: flex; align-items: center; gap: 6px; margin-bottom: 5px; }
      .adv-toast-symbol { font-family: 'Cinzel Decorative', cursive; font-size: 9px; opacity: 0.7; }
      .adv-toast-title { font-family: 'Cinzel', serif; font-size: 10px; font-weight: 700; letter-spacing: 2px; text-transform: uppercase; flex: 1; }
      .adv-toast-rule { height: 1px; margin-bottom: 7px; }
      .adv-toast-message { font-family: 'EB Garamond', serif; font-style: italic; font-size: 14px; color: rgba(74,26,8,0.75); line-height: 1.5; }
      .adv-toast-bottom-rule { height: 1px; margin: 12px 20px 0; background: linear-gradient(90deg,transparent,rgba(201,151,58,0.25),transparent); }
      .adv-toast-close { position: absolute; top: 22px; right: 14px; background: none; border: none; font-size: 13px; cursor: pointer; opacity: 0.45; transition: opacity 0.2s; }
      .adv-toast-close:hover { opacity: 0.9; }

      /* ── MOBILE CARDS ── */
      .adv-card-list { display: flex; flex-direction: column; gap: 12px; padding-top: 4px; }
      .adv-mob-card { background: linear-gradient(145deg, #FDF3E3, #faf0dc); border: 1px solid rgba(201,151,58,0.2); clip-path: polygon(0 10px,10px 0,calc(100% - 10px) 0,100% 10px,100% calc(100% - 10px),calc(100% - 10px) 100%,10px 100%,0 calc(100% - 10px)); box-shadow: 0 2px 12px rgba(26,4,4,0.08); animation: rowIn 0.35s ease both; overflow: hidden; position: relative; }
      .adv-mob-card::before { content: ''; position: absolute; top: 0; left: 0; right: 0; height: 2px; background: linear-gradient(90deg, transparent, rgba(201,151,58,0.5), transparent); }
      .adv-mob-card-header { display: flex; align-items: center; justify-content: space-between; padding: 14px 14px 10px; border-bottom: 1px solid rgba(201,151,58,0.1); }
      .adv-mob-receipt { font-family: 'Cinzel', serif; font-size: 12px; font-weight: 700; color: #8b1a1a; }
      .adv-mob-meta { padding: 10px 14px; display: flex; flex-direction: column; gap: 5px; border-bottom: 1px solid rgba(201,151,58,0.08); }
      .adv-mob-meta-item { display: flex; align-items: flex-start; gap: 7px; font-family: 'EB Garamond', serif; font-size: 13px; color: rgba(74,26,8,0.6); }
      .adv-mob-meta-icon { color: var(--gold-dim); font-size: 11px; margin-top: 2px; flex-shrink: 0; }
      .adv-mob-actions { display: flex; gap: 6px; flex-wrap: wrap; padding: 10px 14px 12px; }
      .adv-mob-btn { display: inline-flex; align-items: center; gap: 5px; font-family: 'Cinzel', serif; font-size: 8.5px; font-weight: 700; letter-spacing: 1px; text-transform: uppercase; border: 1px solid; padding: 7px 12px; cursor: pointer; background: none; transition: all 0.18s; clip-path: polygon(6px 0%,calc(100% - 6px) 0%,100% 50%,calc(100% - 6px) 100%,6px 100%,0 50%); }
      .adv-mob-btn.mob-edit   { border-color: rgba(100,74,140,0.35); color: #6a4a8c; }
      .adv-mob-btn.mob-edit:hover { background: rgba(100,74,140,0.1); }
      .adv-mob-btn.mob-delete { border-color: rgba(139,26,26,0.22); color: rgba(139,26,26,0.7); margin-left: auto; }
      .adv-mob-btn.mob-delete:hover { background: rgba(139,26,26,0.08); color: #8b1a1a; }

      /* ── RESPONSIVE ── */
      .adv-desktop-only { display: table; }
      .adv-mobile-only  { display: none !important; }
      @media (max-width: 768px) {
        .adv-desktop-only { display: none !important; }
        .adv-mobile-only  { display: flex !important; }
        
        .adv-hero { padding: 22px 16px 0; border-radius: 0; }
        .adv-hero-medallion { width: 68px; height: 68px; margin-bottom: 12px; }
        .adv-hero-om { font-size: 24px; }
        .adv-hero-logo-img { width: 40px; height: 40px; }
        .adv-hero-eyebrow { font-size: 7px; letter-spacing: 1.2px; }
        .adv-hero-title { font-size: 19px; }
        .adv-hero-sub { font-size: 13px; margin-bottom: 12px; }
        .adv-stat-row { display: grid; grid-template-columns: 1fr 1fr; gap: 8px; width: 100%; padding: 0 0 18px; }
        .adv-stat-card { min-width: unset; padding: 12px 10px; clip-path: none; border-radius: 8px; }

        .adv-toolbar { padding: 12px 14px 10px; gap: 9px; flex-direction: column; align-items: stretch; }
        .adv-search-wrap { min-width: unset; width: 100%; clip-path: none; border-radius: 28px; }
        .adv-toolbar-btns { width: 100%; display: grid; grid-template-columns: 1fr 1fr; gap: 8px; margin-left: 0; }
        .adv-action-btn { clip-path: none; border-radius: 28px; justify-content: center; padding: 11px 10px; font-size: 9px; }
        .adv-table-wrap { padding: 0 14px 40px; }

        /* Modals slide up */
        .adv-modal-overlay { padding: 0; align-items: flex-end; }
        .adv-regal-modal { clip-path: none !important; border-radius: 22px 22px 0 0 !important; max-height: 93vh; border-bottom: none; width: 100%; max-width: 100% !important; }
        .adv-regal-modal::after { content: ''; position: absolute; top: 8px; left: 50%; transform: translateX(-50%); width: 44px; height: 4px; border-radius: 2px; background: rgba(232,196,106,0.28); pointer-events: none; z-index: 20; display: block; }
        .adv-rm-header { padding: 26px 16px 20px; }
        .adv-detail-scroll-body { padding: 16px 16px 28px; }
        .adv-edit-body { padding: 16px 16px 28px; }
        .adv-edit-grid { grid-template-columns: 1fr; gap: 12px; }
        .adv-edit-grid .full-width { grid-column: span 1; }
        .adv-edit-wrap { clip-path: none; border-radius: 28px; }
        .adv-edit-wrap-textarea { clip-path: none; border-radius: 14px; }
        .adv-edit-save-btn { clip-path: none; border-radius: 28px; flex: 1; justify-content: center; }
        .adv-edit-actions .adv-btn { clip-path: none; border-radius: 28px; }
        .adv-confirm-modal { clip-path: none !important; border-radius: 22px 22px 0 0 !important; max-width: 100%; width: 100%; border-bottom: none; }
        .adv-btn { clip-path: none; border-radius: 28px; padding: 12px 18px; font-size: 9.5px; }
        .adv-toast-container { top: auto; bottom: 14px; right: 14px; left: 14px; }
        .adv-toast-card { width: 100%; clip-path: none; border-radius: 12px; }
      }
      @media (max-width: 390px) {
        .adv-hero-title { font-size: 16px; }
        .adv-hero-eyebrow { display: none; }
      }
    `}</style>
  );
}